'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BottomSheet, Button } from '@/components/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useStore } from '@/providers/StoreProvider';
import { LocationFilterContent } from '@/features/listings/filters/location/LocationFilterContent';
import SelectedNeighborhoodChips from '@/features/listings/filters/location/components/SelectedNeighborhoodChips';
import { NeighborhoodMapCore } from '@/features/listings/filters/location/components/NeighborhoodMapCore';
import type { ExtendedNeighborhood } from '@/features/listings/filters/location/components/NeighborhoodGroupedList';

interface ListingAlertLocationSheetProps {
  open: boolean;
  onClose: () => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedNeighborhoodIds: string[];
  onToggleNeighborhood: (id: string) => void;
  onToggleNeighborhoodWithChildren: (id: string) => void;
  onToggleBorough: (borough: string) => void;
  onReset: () => void;
  onApply: (ids: string[]) => void | Promise<void>;
}

export function ListingAlertLocationSheet({
  open,
  onClose,
  searchQuery,
  onSearchQueryChange,
  selectedNeighborhoodIds,
  onToggleNeighborhood,
  onToggleNeighborhoodWithChildren,
  onToggleBorough,
  onReset,
  onApply,
}: ListingAlertLocationSheetProps) {
  const analytics = useAnalytics();
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods) as ExtendedNeighborhood[];
  const [showMap, setShowMap] = useState(false);
  const isSearching = searchQuery.trim().length > 0;

  useEffect(() => {
    if (!open) {
      setShowMap(false);
    }
  }, [open]);

  const handleNeighborhoodClick = useCallback(
    (neighborhoodId: string) => {
      const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
      if (!neighborhood) return;

      const hasChildren =
        Array.isArray(neighborhood.childrenIds) && neighborhood.childrenIds.length > 0;

      if (hasChildren) {
        onToggleNeighborhoodWithChildren(neighborhoodId);
      } else {
        onToggleNeighborhood(neighborhoodId);
      }
    },
    [neighborhoods, onToggleNeighborhood, onToggleNeighborhoodWithChildren],
  );

  const footer = useMemo(() => {
    if (showMap) {
      return (
        <div className="flex items-center gap-4 bg-primary px-4 py-4">
          <Button
            variant="tertiary"
            className="flex-1"
            disabled={selectedNeighborhoodIds.length === 0}
            onClick={() => {
              analytics.track('listings_filter_allneighborhoods_map_resetselections');
              onReset();
            }}
          >
            Reset
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              analytics.track('listings_filter_allneighborhoods_map_seeresults');
              void onApply(selectedNeighborhoodIds);
            }}
          >
            Done
          </Button>
        </div>
      );
    }

    if (isSearching) return null;

    return (
      <div className="flex items-center gap-4 bg-primary px-4 py-4">
        <Button
          variant="tertiary"
          className="flex-1"
          disabled={selectedNeighborhoodIds.length === 0}
          onClick={() => {
            analytics.track('listings_filter_neighborhood_reset');
            onReset();
          }}
        >
          Reset
        </Button>
        <Button
          className="flex-1"
          onClick={() => {
            analytics.track('listings_filter_neighborhood_seeresults', {
              selectedNeighborhoods: selectedNeighborhoodIds,
            });
            void onApply(selectedNeighborhoodIds);
          }}
        >
          Done
        </Button>
      </div>
    );
  }, [analytics, isSearching, onApply, onReset, selectedNeighborhoodIds, showMap]);

  return (
    <BottomSheet
      label={showMap ? 'All neighborhoods' : 'Select location'}
      fullHeight
      leading={false}
      open={open}
      bodyClassName={showMap ? '!px-0 !py-0' : '!px-2'}
      footer={footer}
      footerClassName="!border-0"
      onClose={onClose}
      {...(showMap
        ? {
            onBack: () => {
              analytics.track('listings_filter_allneighborhoods_map_goback');
              setShowMap(false);
            },
          }
        : {})}
    >
      {showMap ? (
        <div className="flex h-full min-h-0 flex-col">
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <NeighborhoodMapCore
              neighborhoods={neighborhoods}
              selectedNeighborhoodIds={selectedNeighborhoodIds}
              onNeighborhoodClick={handleNeighborhoodClick}
              height="100%"
              width="100%"
            />
          </div>
          {selectedNeighborhoodIds.length > 0 ? (
            <div className="shrink-0 bg-primary py-4 pl-2">
              <SelectedNeighborhoodChips
                mobile
                includeBuilding={false}
                selectedNeighborhoodIds={selectedNeighborhoodIds}
                onToggleNeighborhood={onToggleNeighborhood}
                onToggleNeighborhoodWithChildren={onToggleNeighborhoodWithChildren}
                onToggleBorough={onToggleBorough}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <LocationFilterContent
          searchQuery={searchQuery}
          onSearchQueryChange={onSearchQueryChange}
          variant="mobile"
          onViewMap={() => {
            analytics.track('listings_filter_allneighborhoods_map_seeall');
            setShowMap(true);
          }}
          showSearchInput
          searchPlaceholder="Search neighborhoods"
          showSuggestedLocations={false}
          showBuildingSuggestions={false}
          selectionMode="external"
          selectedNeighborhoodIds={selectedNeighborhoodIds}
          onToggleNeighborhood={onToggleNeighborhood}
          onToggleNeighborhoodWithChildren={onToggleNeighborhoodWithChildren}
          onToggleBorough={onToggleBorough}
          className="h-full"
        />
      )}
    </BottomSheet>
  );
}

export default ListingAlertLocationSheet;
